import React, { useState, useEffect } from 'react';
import { paymentAPI } from '../utils/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { Coins, X, Check, Zap, Crown, Loader2 } from 'lucide-react';
import { track } from '../utils/firebase';

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const CreditPurchase = ({ onClose, onSuccess, currentCredits }) => {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPack, setSelectedPack] = useState(null);

  useEffect(() => {
    loadPacks();
    track.viewPacks();
  }, []);

  const loadPacks = async () => {
    try {
      const res = await paymentAPI.getPacks();
      setPacks(res.data.packs);
    } catch { /* ignore */ }
  };

  const packMeta = {
    '10':  { icon: Coins, color: '#EA580C', bg: '#FFF7ED', popular: false },
    '50':  { icon: Zap,   color: '#7C3AED', bg: '#F5F3FF', popular: true },
    '200': { icon: Crown, color: '#0891B2', bg: '#ECFEFF', popular: false },
  };

  const handlePurchase = async (packId) => {
    if (!RAZORPAY_KEY) {
      toast.error('Payment service not configured');
      return;
    }
    setSelectedPack(packId);
    setLoading(true);
    try {
      const res = await paymentAPI.createOrder(packId);
      const order = res.data;

      const options = {
        key: RAZORPAY_KEY,
        amount: order.amount,
        currency: order.currency,
        name: 'CallChat AI',
        description: order.pack_label,
        order_id: order.order_id,
        handler: async (response) => {
          try {
            await paymentAPI.verifyPayment(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );
            track.paymentSuccess(order.pack_label, order.amount / 100);
            toast.success(`${order.credits} credits added!`);
            onSuccess?.();
          } catch {
            track.paymentFailed('verification_failed');
            toast.error('Payment verification failed');
          }
        },
        theme: { color: '#4338CA' },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create order');
    }
    setLoading(false);
    setSelectedPack(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm" data-testid="credit-purchase-modal">
      <div className="w-full max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom">
        {/* Header */}
        <div className="relative p-6 bg-gradient-to-br from-[#4338CA] to-[#7C3AED] text-white">
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center hover:bg-white/30" data-testid="close-purchase">
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Coins className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold">Get Credits</h2>
              <p className="text-sm text-white/70">Current balance: {currentCredits ?? 0} credits</p>
            </div>
          </div>
        </div>

        {/* Packs */}
        <div className="p-4 space-y-3">
          {packs.map(pack => {
            const meta = packMeta[pack.id] || packMeta['10'];
            const Icon = meta.icon;
            const isSelected = selectedPack === pack.id;
            return (
              <button
                key={pack.id}
                onClick={() => handlePurchase(pack.id)}
                disabled={loading}
                className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all text-left ${
                  meta.popular ? 'border-[#7C3AED] bg-[#F5F3FF] ring-1 ring-[#7C3AED]/20' : 'border-[#E2E8F0] bg-white hover:border-[#CBD5E1]'
                }`}
                data-testid={`buy-pack-${pack.id}`}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: meta.bg }}>
                  <Icon className="w-6 h-6" style={{ color: meta.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#0F172A]">{pack.label}</span>
                    {meta.popular && (
                      <span className="text-[10px] font-bold text-white bg-[#7C3AED] px-1.5 py-0.5 rounded-full">POPULAR</span>
                    )}
                  </div>
                  <p className="text-sm text-[#64748B]">{pack.credits} AI video generations</p>
                </div>
                <div className="text-right shrink-0">
                  {isSelected && loading ? (
                    <Loader2 className="w-5 h-5 text-[#4338CA] animate-spin" />
                  ) : (
                    <span className="text-lg font-bold text-[#0F172A]">&#8377;{pack.price_display}</span>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 pb-4 pt-1">
          <p className="text-xs text-center text-[#94A3B8]">Secure payment via Razorpay. Credits never expire.</p>
        </div>
      </div>
    </div>
  );
};

export default CreditPurchase;
